/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { produce } from "immer";
import isBoolean from "lodash/isBoolean";
import { actionFactory, ActionUnion, payloadAction, simpleAction } from "reductser";

import { ExploreMode, SupportedTheme } from "@vienna/common";

/* -------------------------------------------------- */
/*               Action Types  & Creator              */
/* -------------------------------------------------- */

const actionGlobalUI = {
  // persistent ui settings
  setTheme: payloadAction<SupportedTheme>(),
  setShowExamplePlates: payloadAction<boolean>(),
  // view states and settings
  setExploreMode: payloadAction<ExploreMode>(), // to persist mode
  setWelcomeModalOpen: payloadAction<boolean>(),
  // tracking actions
  enteredNoSaveMode: simpleAction(),
};

export const globalUIActions = actionFactory(actionGlobalUI, "GLOBAL_UI");

type GlobalUIAction = ActionUnion<typeof globalUIActions>;

export interface GlobalUIState {
  uiSettings: UISettings;
  welcomeModalOpen: boolean;
  exploreMode: ExploreMode;
}

interface UISettings {
  theme: SupportedTheme;
  showExamplePlates: boolean; // show examples plates on home
  isScreenshotter: boolean; // only set by screenshotter in browser console, unreachable for normal users
}

interface CustomLocalStorage {
  uiSettings: {
    theme: SupportedTheme;
    showExamplePlates: boolean;
    isScreenshotter: boolean;
  };
}
const uiLocalStorage = (
  typeof window !== "undefined" ? JSON.parse(localStorage?.getItem("uiSettings") || "{}") : "{}"
) as CustomLocalStorage["uiSettings"];
const theme = uiLocalStorage.theme as SupportedTheme;
const isScreenshotter = uiLocalStorage.isScreenshotter;

const getInitialState = (): GlobalUIState => {
  return {
    uiSettings: {
      theme: theme || "light",
      showExamplePlates: isBoolean(uiLocalStorage?.showExamplePlates) ? uiLocalStorage?.showExamplePlates : true,
      isScreenshotter: isScreenshotter,
    },
    welcomeModalOpen: false,
    exploreMode: ExploreMode.random,
  };
};

export const setTheme = (newTheme: SupportedTheme): GlobalUIAction => {
  localStorage.setItem("uiSettings", JSON.stringify({ ...uiLocalStorage, theme: newTheme }));
  return globalUIActions.setTheme(newTheme);
};

export const setShowExamplePlates = (showExamplePlates: boolean): GlobalUIAction => {
  localStorage.setItem("uiSettings", JSON.stringify({ ...uiLocalStorage, showExamplePlates }));
  return globalUIActions.setShowExamplePlates(showExamplePlates);
};

const GlobalUIReducer = (state: GlobalUIState = getInitialState(), action: GlobalUIAction): GlobalUIState =>
  produce(state, (draftState) => {
    if (action.reducer === "GLOBAL_UI") {
      switch (action.type) {
        case "setShowExamplePlates":
          draftState.uiSettings.showExamplePlates = action.payload;
          break;
        case "setTheme":
          draftState.uiSettings.theme = action.payload;
          break;
        case "setWelcomeModalOpen":
          draftState.welcomeModalOpen = action.payload;
          break;
        case "setExploreMode":
          draftState.exploreMode = action.payload;
          break;
        case "enteredNoSaveMode":
        default:
          break;
      }
    }
  });

export default GlobalUIReducer;
